import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { projectsApi } from "@/lib/api";

export default function NewProject() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", country: "", period_start: 2020, period_end: 2025,
    currency: "USD", daily_wage: 10, active_population: 1000000, taxpayers: 500000,
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const p = await projectsApi.create(form);
      toast.success("Projet créé");
      nav(`/app/projects/${p.id}/documents`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-4xl mx-auto w-full" data-testid="new-project-page">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="mb-4" data-testid="back-btn">
        <ArrowLeft size={14} className="mr-2" /> Retour
      </Button>
      <div className="label-mono text-[10px] text-muted-foreground">NOUVEAU PROJET</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Créer un projet d'analyse</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Renseignez le pays et la période. Le <strong>salaire journalier de référence (Sj)</strong>, la population active
        et le nombre de contribuables sont requis pour les calculs vitalistes D1/D2/D4/D5/V5/V6.
      </p>

      <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-6 border border-border bg-card p-8 rounded-sm" data-testid="new-project-form">
        <div className="md:col-span-2">
          <Label htmlFor="name">Nom du projet *</Label>
          <Input id="name" data-testid="new-project-name" required value={form.name} onChange={set("name")}
                 placeholder="Ex. Mauritanie — Analyse dette 2020-2025" className="mt-1.5" />
        </div>

        <div>
          <Label htmlFor="country">Pays *</Label>
          <Input id="country" data-testid="new-project-country" required value={form.country} onChange={set("country")}
                 placeholder="Mauritanie" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="currency">Devise</Label>
          <Input id="currency" data-testid="new-project-currency" value={form.currency} onChange={set("currency")}
                 placeholder="USD" className="mt-1.5" />
        </div>

        <div>
          <Label htmlFor="ps">Période début</Label>
          <Input id="ps" data-testid="new-project-period-start" type="number" min="1900" max="2100"
                 value={form.period_start} onChange={setNum("period_start")} className="mt-1.5 font-mono" />
        </div>
        <div>
          <Label htmlFor="pe">Période fin</Label>
          <Input id="pe" data-testid="new-project-period-end" type="number" min="1900" max="2100"
                 value={form.period_end} onChange={setNum("period_end")} className="mt-1.5 font-mono" />
        </div>

        <div className="md:col-span-2 border-t border-border my-2"></div>
        <div className="md:col-span-2">
          <div className="label-mono text-[10px] text-gold">PARAMÈTRES VITALISTES</div>
          <p className="text-xs text-muted-foreground mt-1">
            Saisissez ces valeurs avec soin — elles conditionnent toutes les formules D1/D2/D5/V5/V6.
          </p>
        </div>

        <div>
          <Label htmlFor="sj">Salaire journalier de réf. (Sj) — en {form.currency} *</Label>
          <Input id="sj" data-testid="new-project-daily-wage" type="number" step="0.01" min="0.01" required
                 value={form.daily_wage} onChange={setNum("daily_wage")} className="mt-1.5 font-mono" />
        </div>
        <div>
          <Label htmlFor="pop">Population active totale *</Label>
          <Input id="pop" data-testid="new-project-population" type="number" min="1" required
                 value={form.active_population} onChange={setNum("active_population")} className="mt-1.5 font-mono" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="tax">Nombre de contribuables actifs *</Label>
          <Input id="tax" data-testid="new-project-taxpayers" type="number" min="1" required
                 value={form.taxpayers} onChange={setNum("taxpayers")} className="mt-1.5 font-mono" />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="desc">Description (optionnel)</Label>
          <Textarea id="desc" data-testid="new-project-description" value={form.description} onChange={set("description")}
                    placeholder="Objectif, portée, contexte…" className="mt-1.5 min-h-[80px]" />
        </div>

        <div className="md:col-span-2 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>Annuler</Button>
          <Button type="submit" disabled={loading} className="bg-marine hover:bg-marine/90 text-white"
                  data-testid="new-project-submit-btn">
            {loading ? "Création…" : "Créer le projet"}
          </Button>
        </div>
      </form>
    </div>
  );
}
